import { Check, X, Sparkles, Crown, Star, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SEAL = "var(--seal)";
const INK = "var(--ink)";
const INK_55 = "var(--ink-55)";
const INK_72 = "var(--ink-72)";

const TIERS = [
  {
    id: "starter",
    name: "Starter",
    eyebrow: "i. To begin",
    italic: "free",
    badge: "Free forever",
    price: "₹0",
    period: "always",
    blurb: "Perfect to capture sparks and try the workflow.",
    icon: Sparkles,
    cta: "Start free",
    ctaRoute: "/",
    accent: false,
    comingSoon: false,
    features: {
      "Ideas in Vault": "25",
      "Pipeline (6 stages)": "✓",
      "AI scripts / month": "5",
      "Swipe inspirations": "30",
      "Active goals": "1",
      "Calendar + Streak": "✓",
      "Manual analytics": "✓",
      "CSV export": "✓",
      "Real platform analytics": "—",
      "AI thumbnails (Nano Banana)": "—",
      "AI captions": "—",
      "Public Creator Profile": "—",
      "Email reminders": "—",
      "Team seats": "—",
      "Support": "Community",
    },
  },
  {
    id: "creator",
    name: "Creator",
    eyebrow: "ii. Most popular",
    italic: "₹499 / mo",
    badge: "Most popular",
    price: "₹499",
    period: "/month",
    altPrice: "₹4,999/year (save 16%) · ₹5,999 lifetime",
    bestFor: "Best for creators publishing 4–20 pieces of content per month.",
    blurb: "Everything needed to go from idea to published content.",
    icon: Star,
    cta: "Coming soon",
    ctaRoute: "/checkout?plan=creator",
    accent: true,
    comingSoon: true,
    features: {
      "Ideas in Vault": "250",
      "Pipeline (6 stages)": "✓",
      "AI scripts / month": "40",
      "Swipe inspirations": "300",
      "Active goals": "8",
      "Calendar + Streak": "✓",
      "Manual analytics": "✓",
      "CSV export": "✓",
      "Real platform analytics": "YouTube + Instagram",
      "AI thumbnails (Nano Banana)": "10 / mo",
      "AI captions": "40 / mo",
      "Public Creator Profile": "✓",
      "Email reminders": "✓ (streak + scheduled)",
      "Team seats": "1 (just you)",
      "Support": "Priority email",
    },
  },
  {
    id: "studio",
    name: "Studio",
    eyebrow: "iii. For pros",
    italic: "₹999 / mo",
    badge: "Top tier",
    price: "₹999",
    period: "/month",
    altPrice: "₹9,999/year (save 17%)",
    bestFor: "For full-time creators and small creator teams (2–3 people).",
    blurb: "Premium tools + community + early access.",
    icon: Crown,
    cta: "Coming soon",
    ctaRoute: "/checkout?plan=studio",
    accent: false,
    comingSoon: true,
    features: {
      "Ideas in Vault": "2,000",
      "Pipeline (6 stages)": "✓",
      "AI scripts / month": "200",
      "Swipe inspirations": "2,000",
      "Active goals": "30",
      "Calendar + Streak": "✓",
      "Manual analytics": "✓",
      "CSV export": "✓",
      "Real platform analytics": "YouTube + Instagram + X + TikTok",
      "AI thumbnails (Nano Banana)": "60 / mo",
      "AI captions": "200 / mo",
      "Public Creator Profile": "✓ (custom domain)",
      "Email reminders": "✓ (full automation)",
      "Team seats": "3",
      "Priority feature requests": "✓",
      "Early access to new tools": "✓",
      "Founder community access": "✓",
      "Support": "Priority chat + onboarding call",
    },
  },
];

function isYes(v) {
  return v === "✓";
}

function FeatureRow({ label, values }) {
  return (
    <div className="grid grid-cols-4 items-center py-3" style={{ borderTop: "1px solid var(--ink-22)" }}>
      <div className="text-sm" style={{ color: INK_72 }}>{label}</div>
      {values.map((v, i) => (
        <div key={i} className="text-sm text-center" style={{ color: v === "—" ? "var(--ink-38)" : INK }}>
          {isYes(v) ? <Check size={14} style={{ color: SEAL, margin: "0 auto" }} /> :
           v === "—" ? <X size={14} style={{ color: "var(--ink-38)", margin: "0 auto" }} /> :
           <span className="text-xs">{v}</span>}
        </div>
      ))}
    </div>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  // Union of keys across all tiers so premium-only rows (Studio's
  // "Priority feature requests" etc.) still appear in the comparison table.
  const featureKeys = Array.from(
    new Set(TIERS.flatMap((t) => Object.keys(t.features)))
  );

  return (
    <div className="min-h-screen px-5 md:px-10 py-6 md:py-10" style={{ background: "var(--bg)", color: "var(--ink)" }}>
      {/* Minimal public header */}
      <div className="max-w-6xl mx-auto mb-12 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5 hover:opacity-80 transition" data-testid="pricing-home-link">
          <span className="seal-dot" />
          <span className="font-heading text-lg" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500" }}>PintaFlow</span>
        </button>
        <button onClick={() => navigate("/dashboard")} className="btn-secondary text-xs" data-testid="pricing-signin-link">
          Sign in
        </button>
      </div>
    <div className="max-w-6xl mx-auto pb-12">
      {/* Header */}
      <div className="text-center mb-12 md:mb-16">
        <div className="eyebrow inline-flex items-center justify-center mb-5">
          <span className="seal-dot mr-2" /> The pricing
        </div>
        <h1
          className="font-heading text-4xl md:text-6xl leading-[1.04]"
          style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}
        >
          Three rooms.<br />
          <span className="font-display-italic">Same craft.</span>
        </h1>
        <p className="text-sm md:text-base mt-5 max-w-xl mx-auto leading-relaxed" style={{ color: INK_72 }}>
          Start free, upgrade when your work needs the bigger room. Honest limits, no surprises — every plan publishes the same calm studio.
        </p>
      </div>

      {/* Three cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {TIERS.map((tier) => {
          const Icon = tier.icon;
          return (
            <div
              key={tier.id}
              data-testid={`pricing-tier-${tier.id}`}
              className="rounded-2xl p-7 relative flex flex-col"
              style={{
                background: tier.accent
                  ? "linear-gradient(160deg, rgba(216,168,87,0.10), rgba(216,168,87,0.02) 70%)"
                  : "var(--surface)",
                border: tier.accent ? "1px solid rgba(216,168,87,0.45)" : "1px solid var(--ink-22)",
                boxShadow: tier.accent ? "0 30px 60px rgba(0,0,0,0.35)" : "none",
              }}
            >
              {tier.accent && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em]"
                  style={{ background: "var(--seal)", color: "var(--bg)" }}
                >
                  {tier.badge}
                </div>
              )}

              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} style={{ color: SEAL }} strokeWidth={1.6} />
                <div className="eyebrow">{tier.eyebrow}</div>
              </div>

              <h3 className="font-heading text-2xl mb-1" style={{ fontVariationSettings: "'opsz' 48, 'wght' 500" }}>
                {tier.name}
              </h3>

              <div className="flex items-baseline gap-1 mt-3">
                <span className="font-heading text-4xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
                  {tier.price}
                </span>
                <span className="text-xs" style={{ color: INK_55 }}>{tier.period}</span>
              </div>
              {tier.altPrice && (
                <div className="text-[11px] mt-1" style={{ color: SEAL }}>{tier.altPrice}</div>
              )}

              {tier.bestFor && (
                <div
                  className="mt-4 px-3 py-2 rounded-lg text-xs"
                  style={{
                    background: tier.accent ? "rgba(216,168,87,0.08)" : "var(--surface-2, rgba(0,0,0,0.20))",
                    border: tier.accent ? "1px solid rgba(216,168,87,0.25)" : "1px solid var(--ink-22)",
                    color: INK_72,
                  }}
                  data-testid={`pricing-bestfor-${tier.id}`}
                >
                  {tier.bestFor}
                </div>
              )}

              <p className="text-sm mt-4 leading-relaxed" style={{ color: INK_72 }}>{tier.blurb}</p>

              <div className="mt-6 space-y-2.5 flex-1">
                {(() => {
                  // Hand-curated headline bullets (in priority order)
                  const baseBullets = [
                    `${tier.features["Ideas in Vault"]} ideas in Vault`,
                    `${tier.features["AI scripts / month"]} AI scripts / month`,
                    tier.features["Real platform analytics"] === "—"
                      ? "Manual analytics only"
                      : `Real-time ${tier.features["Real platform analytics"]} analytics`,
                    tier.features["AI thumbnails (Nano Banana)"] === "—"
                      ? null
                      : `${tier.features["AI thumbnails (Nano Banana)"]} AI thumbnails`,
                    tier.features["Public Creator Profile"] === "✓"
                      ? "Public Creator Profile"
                      : tier.features["Public Creator Profile"] === "—"
                      ? null
                      : tier.features["Public Creator Profile"],
                    tier.features["Team seats"] === "—"
                      ? null
                      : `${tier.features["Team seats"]} team seat${tier.features["Team seats"] === "1 (just you)" ? "" : "s"}`,
                    tier.features["Support"],
                  ].filter(Boolean);
                  // Append any tier-only "perk" features (value === "✓") that
                  // aren't already covered above — keeps the card honest when
                  // a tier ships premium-only perks.
                  const handled = new Set([
                    "Ideas in Vault", "AI scripts / month", "Real platform analytics",
                    "AI thumbnails (Nano Banana)", "Public Creator Profile",
                    "Team seats", "Support", "Pipeline (6 stages)", "Swipe inspirations",
                    "Active goals", "Calendar + Streak", "Manual analytics", "CSV export",
                    "AI captions", "Email reminders",
                  ]);
                  const perks = Object.entries(tier.features)
                    .filter(([k, v]) => v === "✓" && !handled.has(k))
                    .map(([k]) => k);
                  return [...baseBullets, ...perks].map((feat, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm" style={{ color: INK_72 }}>
                      <Check size={13} style={{ color: SEAL, marginTop: 4 }} strokeWidth={1.6} />
                      <span>{feat}</span>
                    </div>
                  ));
                })()}
              </div>

              <button
                onClick={() => { if (!tier.comingSoon) navigate(tier.ctaRoute); }}
                disabled={tier.comingSoon}
                data-testid={`pricing-cta-${tier.id}`}
                className="mt-7 rounded-full w-full py-3 transition hover:opacity-90 inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-70"
                style={{
                  background: tier.comingSoon ? "transparent" : (tier.accent ? "var(--ink)" : "transparent"),
                  color: tier.comingSoon ? INK_55 : (tier.accent ? "var(--bg)" : INK),
                  border: tier.comingSoon ? "1px dashed var(--ink-22)" : (tier.accent ? "1px solid var(--ink)" : "1px solid var(--ink-22)"),
                }}
              >
                <span className="text-[13px] font-medium tracking-tight">{tier.cta}</span>
                {!tier.comingSoon && <ArrowRight size={13} />}
              </button>
            </div>
          );
        })}
      </div>

      {/* Full feature table */}
      <div className="mt-16">
        <div className="eyebrow mb-3">Full feature breakdown</div>
        <h2 className="font-heading text-2xl md:text-3xl mb-6" style={{ fontVariationSettings: "'opsz' 64, 'wght' 450" }}>
          Every limit, <span className="font-display-italic">visible.</span>
        </h2>
        <div className="card">
          <div className="grid grid-cols-4 pb-3" style={{ borderBottom: "1px solid var(--ink-22)" }}>
            <div className="eyebrow">Feature</div>
            {TIERS.map((t) => (
              <div key={t.id} className="text-center">
                <div className="eyebrow" style={{ color: t.accent ? SEAL : INK_55 }}>{t.name}</div>
                <div className="text-xs mt-1 font-mono" style={{ color: INK_55 }}>{t.price}</div>
              </div>
            ))}
          </div>
          {featureKeys.map((k) => (
            <FeatureRow key={k} label={k} values={TIERS.map((t) => t.features[k] ?? "—")} />
          ))}
        </div>
      </div>

      {/* Lifetime deal banner */}
      <div
        className="mt-12 rounded-2xl p-6 md:p-8 text-center"
        style={{
          background: "linear-gradient(135deg, rgba(216,168,87,0.12), rgba(216,168,87,0.04))",
          border: "1px solid rgba(216,168,87,0.40)",
        }}
        data-testid="pricing-lifetime-banner"
      >
        <div className="eyebrow mb-3">Pre-launch · first 100 only</div>
        <h2 className="font-heading text-2xl md:text-3xl" style={{ fontVariationSettings: "'opsz' 64, 'wght' 450" }}>
          The <span className="font-display-italic">founder</span> deal — ₹5,999 once.
        </h2>
        <p className="text-sm mt-3 max-w-xl mx-auto" style={{ color: INK_72 }}>
          Lifetime access to the <span style={{ color: INK }}>Creator</span> tier. No subscriptions. No renewals. Available to the first 100 creators only — about 80 spots remaining.
        </p>
        <button
          disabled
          data-testid="pricing-lifetime-cta"
          className="mt-5 rounded-full px-6 py-3 inline-flex items-center gap-2 transition disabled:cursor-not-allowed disabled:opacity-80"
          style={{ background: "transparent", color: SEAL, border: `1px dashed ${SEAL}` }}
        >
          <span className="text-[13px] font-medium tracking-tight">Lifetime checkout — coming soon</span>
        </button>
      </div>

      {/* FAQ */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          ["What if I hit a limit?", "We'll never block your existing work. You'll see a quiet prompt to upgrade — but every idea, every script you've created stays yours."],
          ["Can I downgrade later?", "Yes. Switch any time, prorated. Data is never deleted on downgrade — older items just become read-only above the new tier's cap."],
          ["Do limits reset?", "AI scripts, thumbnails, and captions reset on the 1st of each calendar month."],
          ["Why limits, not 'unlimited'?", "Because honest limits keep PintaFlow fast for everyone. Real platform APIs (YouTube, Instagram) also charge us per call — so we charge fairly, not infinitely."],
        ].map(([q, a], i) => (
          <div key={i} className="card">
            <div className="font-heading text-lg mb-2" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500" }}>{q}</div>
            <div className="text-sm leading-relaxed" style={{ color: INK_72 }}>{a}</div>
          </div>
        ))}
      </div>
    </div>
    </div>
  );
}
